import { useCallback, useRef, useState } from 'react';

import { createDataset, getDataset } from '../../../services/datasets.js';
import { buildDatasetRequest, selectionSignature } from '../utils/datasetSelection.js';

/**
 * The DATASET the Data step's selection publishes (CT-5725).
 *
 * The rev4 flow builds a widget **on top of a published record set**, so the
 * selection has to exist as a dataset before the widget can name its columns.
 * This asks for it when the author moves on to Configure, and again before a
 * save, which is where the two things that can go stale meet: the author may
 * have gone back and changed a column, and they may have reached Configure by
 * clicking the tab rather than the button.
 *
 * Asking twice is free. `/dataset/create` is idempotent on the selection —
 * the same fields and filters answer with the same `datasetUuid` and
 * `created: false` — and this keeps the signature of what it asked for, so
 * the usual case does not even reach the network.
 *
 * Every call answers `{ dataset, error }`: a selection with no entity and no
 * dataset — a widget still being started — yields both `null` and makes no
 * request, since there is nothing to resolve. The refusal travels with the answer because the `error`
 * state below belongs to the *next* render — a caller that reads it decides on
 * the previous call's outcome, not its own.
 */
/** Nothing to publish — a selection with no entity, or one still being made. */
const NOTHING_TO_PUBLISH = Object.freeze({ dataset: null, error: null });

export const usePublishedDataset = ({ form, columns }) => {
  const [isPreparing, setIsPreparing] = useState(false);
  const [error, setError] = useState(null);
  /** `{ key, promise }` — what is being asked for, not merely that something is. */
  const inFlight = useRef(null);
  /** Which request may still write to the form; a later one retires it. */
  const ticketRef = useRef(0);

  const { dataset, datasetRef, setDatasetRef } = form;
  const isEntitySource = dataset.sourceType === 'entity';

  /*
    One runner for both paths, because both can be overtaken.

    `inFlight` used to hold a bare promise and any second call joined it,
    whatever it was for: choosing dataset A and then B while A was still open
    handed B's caller A's columns and A's uuid, and the form bound the wrong
    source (Bugbot #596). It is keyed now, so only a call asking for the same
    thing waits on it.

    The ticket is the other half. A request that has been overtaken must not
    write at all — it would put the source the author just left back on the
    form — so it resolves as "nothing to publish", which is what it has
    become. The caller that superseded it is the one that answers.
  */
  const run = useCallback((key, read) => {
    if (inFlight.current?.key === key) return inFlight.current.promise;

    const ticket = ticketRef.current + 1;
    ticketRef.current = ticket;
    const current = () => ticket === ticketRef.current;

    setIsPreparing(true);
    setError(null);

    const promise = read()
      .then((answer) => {
        if (!current()) return NOTHING_TO_PUBLISH;
        setDatasetRef(answer);
        return { dataset: answer, error: null };
      })
      .catch((reason) => {
        if (!current()) return NOTHING_TO_PUBLISH;
        setError(reason);
        return { dataset: null, error: reason };
      })
      .finally(() => {
        if (inFlight.current?.key === key) inFlight.current = null;
        if (current()) setIsPreparing(false);
      });

    inFlight.current = { key, promise };
    return promise;
  }, [setDatasetRef]);

  const ensureDataset = useCallback(async () => {
    /*
      A dataset the author picked themselves is already published; what is
      missing is its column list, and the bindings cannot name a `fieldKey`
      without it (CT-5860). So this path *reads* where the entity path
      *creates*, and both end with the same `datasetRef`.
    */
    if (!isEntitySource) {
      const chosen = dataset.datasetId;
      if (!chosen) return NOTHING_TO_PUBLISH;
      if (datasetRef?.datasetUuid === chosen) return { dataset: datasetRef, error: null };

      return run(`dataset:${chosen}`, () => getDataset(chosen));
    }

    const request = buildDatasetRequest(form, { columns });
    if (!request) return NOTHING_TO_PUBLISH;

    const signature = selectionSignature(request);
    if (datasetRef?.signature === signature) return { dataset: datasetRef, error: null };

    return run(
      `selection:${signature}`,
      () => createDataset(request).then((published) => ({ ...published, signature })),
    );
  }, [columns, dataset.datasetId, datasetRef, form, isEntitySource, run]);

  return { ensureDataset, isPreparing, error };
};

export default usePublishedDataset;
